import DoctorDetail from "issara-sdk/apis/DoctorDetail_core";
import InvoiceItemList from "issara-sdk/apis/InvoiceItemList_apps_BIL";
import ReceiptDetail from "issara-sdk/apis/ReceiptDetail_apps_BILM";
import ReceiptList from "issara-sdk/apis/ReceiptList_apps_BIL";
import UserPermissionView from "issara-sdk/apis/UserPermissionView_users";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import type { HistoryFilter, Receipt, ReceiptItem } from "../../patient/billingHistoryModelV4";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  SELECT: "SELECT",
  PRINT: "PRINT",
  CANCEL: "CANCEL",
} as const;

export const SEQUENCE_NAME = "BillingHistoryV4" as const;

export const CARD_NAME = "BillingHistoryTabV4" as const;

/** =============== Types and Interfaces ===============  */
export type CancelReceiptData = { username: string; password: string; note: string };

export type State = Partial<{
  BillingHistoryV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    patientId: number;
    filter: HistoryFilter;
    receipts: Receipt[];
    selectedId: number | null;
    items: ReceiptItem[];
    // config_BIL_CAN_PRINT_RECEIPT_COPY
    canPrintCopy: boolean;
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage" | "masterOptions">;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "GetMasterData"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { patientId: number; filter: HistoryFilter; keepSelected?: boolean };
  [ACTIONS.SELECT]: { receipt: Receipt | null };
  [ACTIONS.PRINT]: { receipt: Receipt; note?: string; onDone?: () => void };
  [ACTIONS.CANCEL]: { receipt: Receipt; data: CancelReceiptData; onDone?: () => void };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type ApiResult = [any, any, any?];

/** ===================== Constants ====================  */
export const StateInitial: State = {
  BillingHistoryV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: [
        ["cancel_receipt", {}],
        ["prenameEn", {}],
      ],
    },
  });

  controller.setState(
    {
      BillingHistoryV4Sequence: {
        sequenceIndex: "Action",
        receipts: [],
        selectedId: null,
        items: [],
        canPrintCopy: false,
      },
    },
    () => {
      if ("action" in params) {
        Action(controller, params as unknown as ActionType);
      }
    },
  );

  const [permission]: ApiResult = await UserPermissionView.post({
    apiToken: controller.apiToken,
    data: { config_BIL_CAN_PRINT_RECEIPT_COPY: false },
  });

  const state = controller.getState();

  controller.setState({
    BillingHistoryV4Sequence: {
      ...state.BillingHistoryV4Sequence,
      canPrintCopy: !!permission?.config_BIL_CAN_PRINT_RECEIPT_COPY,
    },
  });
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.SELECT]: HandleSelect,
    [ACTIONS.PRINT]: HandlePrint,
    [ACTIONS.CANCEL]: HandleCancel,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  let state = controller.getState();
  const samePatient = state.BillingHistoryV4Sequence?.patientId === params.patientId;

  controller.setState({
    BillingHistoryV4Sequence: {
      ...state.BillingHistoryV4Sequence,
      patientId: params.patientId,
      filter: params.filter,
      receipts: samePatient ? (state.BillingHistoryV4Sequence?.receipts ?? []) : [],
      ...(!params.keepSelected && { selectedId: null, items: [] }),
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.SEARCH]: null },
  });

  const [res, error]: ApiResult = await ReceiptList.list({
    apiToken: controller.apiToken,
    params: {
      patient: params.patientId,
      from_date: params.filter.fromDate,
      to_date: params.filter.toDate,
      ...(params.filter.status && { status: params.filter.status }),
      ...(params.filter.code.trim() && { sub_invoice_code: params.filter.code.trim() }),
    },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.SEARCH, error });

    return;
  }

  state = controller.getState();

  if (state.BillingHistoryV4Sequence?.patientId !== params.patientId) {
    return;
  }

  controller.setState({
    BillingHistoryV4Sequence: { ...state.BillingHistoryV4Sequence, receipts: res?.items ?? [] },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

/** เลือกใบเสร็จ: โหลดรายการค่าบริการของใบนั้น (null = ปิดรายละเอียด) */
const HandleSelect: Handler<Params<Actions["SELECT"]>> = async (controller, params) => {
  let state = controller.getState();
  const receiptId = params.receipt?.id ?? null;

  controller.setState({
    BillingHistoryV4Sequence: {
      ...state.BillingHistoryV4Sequence,
      selectedId: receiptId,
      items: [],
    },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.SELECT]: null },
  });

  if (!receiptId) {
    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.SELECT]: BLC_STATUS.LOADING,
    },
  });

  const [res, error]: ApiResult = await InvoiceItemList.list({
    apiToken: controller.apiToken,
    params: { receipt: receiptId },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.SELECT, error });

    return;
  }

  state = controller.getState();

  if (state.BillingHistoryV4Sequence?.selectedId !== receiptId) {
    return;
  }

  controller.setState({
    BillingHistoryV4Sequence: { ...state.BillingHistoryV4Sequence, items: res?.items ?? [] },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SELECT]: BLC_STATUS.SUCCESS },
  });
};

/** พิมพ์ใบเสร็จต้นฉบับ (ไม่มี note) / สำเนา (มี note + ลายน้ำ) */
const HandlePrint: Handler<Params<Actions["PRINT"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PRINT]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.PRINT]: null },
  });

  const [doctor]: ApiResult = await DoctorDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.receipt.doctor,
  });

  const prenameEn = (state.masterOptions?.prenameEn ?? []).find(
    (option: { value: unknown; text: string }) => option.value === doctor?.pre_name_en,
  )?.text;

  const doctorName =
    doctor?.pre_name_en && doctor?.first_name_en && doctor?.last_name_en
      ? `${doctor.full_name}  (${prenameEn} ${doctor.first_name_en} ${doctor.last_name_en})`
      : doctor?.full_name;

  const { HandlePrintReceipt } = await import("react-lib/apps/HISV3/BIL/sequence/BillPayment");

  await HandlePrintReceipt(controller as unknown as Parameters<typeof HandlePrintReceipt>[0], {
    id: params.receipt.id,
    doctorName,
    invoice: params.receipt.invoice,
    isMeBilling: true,
    ...(params.note ? { note: params.note, isWatermark: true } : { isShowBackground: true }),
  });

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.PRINT]: BLC_STATUS.SUCCESS,
    },
  });

  params.onDone?.();
};

/** ยกเลิกใบเสร็จ แล้วค้นหาใหม่โดยเปิดใบเดิมค้างไว้ */
const HandleCancel: Handler<Params<Actions["CANCEL"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.CANCEL]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.CANCEL]: null },
  });

  const [, error]: ApiResult = await ReceiptDetail.delete({
    apiToken: controller.apiToken,
    pk: params.receipt.id,
    data: params.data,
    extra: { device: controller.data.device, division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.CANCEL, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.CANCEL]: BLC_STATUS.SUCCESS,
    },
  });

  params.onDone?.();

  const { patientId, filter } = controller.getState().BillingHistoryV4Sequence ?? {};

  if (!patientId || !filter) {
    return;
  }

  await HandleSearch(controller, {
    sequence: SEQUENCE_NAME,
    action: ACTIONS.SEARCH,
    patientId,
    filter,
    keepSelected: true,
  });
};
